import {
  LocalForageTanStackQueryCacheClient,
  persistedGrpcQueryCacheStore,
} from "@/utils/connect-query/query-cache-persistence/localforage-query-cache-client";
import { getPersistedGrpcQueryEndpoint } from "@/utils/connect-query/query-cache-persistence/persisted-grpc-query-endpoints";
import { setCurrentGrpcQueryPersistenceScope } from "@/utils/connect-query/query-cache-persistence/persisted-grpc-query-scope";
import {
  makeDehydratedQueryFromPersistedGrpcRecord,
  PersistedGrpcQueryRecord,
} from "@/utils/connect-query/query-cache-persistence/persisted-grpc-query-record";
import { hydrate, QueryClient, QueryKey } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

export const DEFAULT_PERSISTED_GRPC_QUERY_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface PreloadPersistedGrpcQueryCacheOptions {
  scope: string;
  store?: LocalForageTanStackQueryCacheClient;
  maxAgeMs?: number;
  now?: number;
}

export interface PreloadPersistedGrpcQueryCacheResult {
  hydrated: number;
  hydratedQueryKeys: QueryKey[];
  skipped: number;
  removed: number;
}

export async function preloadPersistedGrpcQueryCache(
  queryClient: QueryClient,
  {
    scope,
    store = persistedGrpcQueryCacheStore,
    maxAgeMs = DEFAULT_PERSISTED_GRPC_QUERY_MAX_AGE_MS,
    now = Date.now(),
  }: PreloadPersistedGrpcQueryCacheOptions,
): Promise<PreloadPersistedGrpcQueryCacheResult> {
  markPerformance(RESTORE_START_MARK);
  const { values: records, expired } = await store.getAllValuesForScope<PersistedGrpcQueryRecord>(
    scope,
    { maxAgeMs, now },
  );
  markPerformance(RESTORE_READ_MARK);
  measurePerformance("grpc-query-cache:restore-read", RESTORE_START_MARK, RESTORE_READ_MARK);
  const queries = [];
  const hydratedQueryKeys: QueryKey[] = [];
  const removedQueryHashes: string[] = [];
  let skipped = 0;

  for (const record of records) {
    // The index's persistedAt already filtered expired entries; this guards a record whose own
    // timestamp disagrees with it.
    if (now - record.persistedAt > maxAgeMs) {
      removedQueryHashes.push(record.queryHash);
      continue;
    }

    const endpoint = getPersistedGrpcQueryEndpoint(record.serviceName, record.methodName);
    if (endpoint == null) {
      removedQueryHashes.push(record.queryHash);
      continue;
    }

    const dehydratedQuery = makeDehydratedQueryFromPersistedGrpcRecord(record, endpoint);
    if (dehydratedQuery == null) {
      skipped += 1;
      continue;
    }

    queries.push(dehydratedQuery);
    hydratedQueryKeys.push(dehydratedQuery.queryKey);
  }

  if (queries.length > 0) {
    hydrate(queryClient, { mutations: [], queries });
  }

  await store.removeValuesByQueryHashes(scope, removedQueryHashes);
  markPerformance(RESTORE_END_MARK);
  measurePerformance("grpc-query-cache:restore", RESTORE_START_MARK, RESTORE_END_MARK);

  return {
    hydrated: queries.length,
    hydratedQueryKeys,
    skipped,
    removed: expired + removedQueryHashes.length,
  };
}

const RESTORE_START_MARK = "grpc-query-cache:restore-start";
const RESTORE_READ_MARK = "grpc-query-cache:restore-read-end";
const RESTORE_END_MARK = "grpc-query-cache:restore-end";

function markPerformance(name: string): void {
  if (typeof performance !== "undefined" && typeof performance.mark === "function") {
    performance.mark(name);
  }
}

function measurePerformance(name: string, startMark: string, endMark: string): void {
  if (typeof performance === "undefined" || typeof performance.measure !== "function") {
    return;
  }
  try {
    performance.measure(name, startMark, endMark);
  } catch {
    // A mark may be missing if the buffer was cleared; measurement is best-effort.
  }
}

/**
 * Sweeps expired entries of every scope and orphaned payloads once the browser is idle, so the
 * whole-store scan never competes with restore or the first page RPCs.
 */
function scheduleStaleEntryCleanup(
  store: LocalForageTanStackQueryCacheClient,
  maxAgeMs: number,
  onStart: () => void,
): () => void {
  const run = () => {
    onStart();
    void store.removeStaleEntries({ maxAgeMs }).catch(() => undefined);
  };
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(run, { timeout: STALE_ENTRY_CLEANUP_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const timeoutId = window.setTimeout(run, STALE_ENTRY_CLEANUP_TIMEOUT_MS);
  return () => window.clearTimeout(timeoutId);
}

const STALE_ENTRY_CLEANUP_TIMEOUT_MS = 10_000;

export interface UsePreloadPersistedGrpcQueryCacheOptions {
  scope: string | null;
  waitForScope: boolean;
}

/**
 * This is designed to be a singleton, only used once in the root of the project in _app.tsx,
 * rendered by the NetworkingWrapper.
 */
export function usePreloadPersistedGrpcQueryCache(
  queryClient: QueryClient,
  { scope, waitForScope }: UsePreloadPersistedGrpcQueryCacheOptions,
): boolean {
  const hydratedScopesRef = useRef(new Set<string>());
  const hydratedQueryKeysToRefetchRef = useRef<QueryKey[]>([]);
  const [backgroundRefetchVersion, setBackgroundRefetchVersion] = useState(0);
  const [isRestoring, setIsRestoring] = useState(
    () => typeof window !== "undefined" && (waitForScope || scope != null),
  );

  useEffect(() => {
    if (waitForScope) {
      setCurrentGrpcQueryPersistenceScope(null);
      setIsRestoring(true);
      return;
    }

    setCurrentGrpcQueryPersistenceScope(scope);
    if (scope == null) {
      setIsRestoring(false);
      return;
    }

    if (hydratedScopesRef.current.has(scope)) {
      setIsRestoring(false);
      return;
    }

    let cancelled = false;
    setIsRestoring(true);
    const restoreStartMark = markPersistedGrpcQueryRestoreStart();

    void preloadPersistedGrpcQueryCache(queryClient, { scope })
      .then((result) => {
        if (!cancelled) {
          hydratedQueryKeysToRefetchRef.current = result.hydratedQueryKeys;
        }
      })
      .finally(() => {
        measurePersistedGrpcQueryRestore(restoreStartMark);
        if (!cancelled) {
          hydratedScopesRef.current.add(scope);
          setIsRestoring(false);
          setBackgroundRefetchVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [queryClient, scope, waitForScope]);

  useEffect(() => {
    if (isRestoring || hydratedQueryKeysToRefetchRef.current.length === 0) {
      return;
    }

    const queryKeys = hydratedQueryKeysToRefetchRef.current;
    hydratedQueryKeysToRefetchRef.current = [];

    const timeoutId = window.setTimeout(() => {
      queryKeys.forEach((queryKey) => {
        void queryClient.refetchQueries({ queryKey, exact: true, type: "active" });
      });
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [backgroundRefetchVersion, isRestoring, queryClient]);

  // A sweep cancelled before it starts (restore re-entered, unmount) is rescheduled on the next
  // idle period after restore; once started it runs at most once per page load.
  const staleEntryCleanupStartedRef = useRef(false);
  useEffect(() => {
    if (isRestoring || staleEntryCleanupStartedRef.current) {
      return;
    }
    return scheduleStaleEntryCleanup(
      persistedGrpcQueryCacheStore,
      DEFAULT_PERSISTED_GRPC_QUERY_MAX_AGE_MS,
      () => {
        staleEntryCleanupStartedRef.current = true;
      },
    );
  }, [isRestoring]);

  return isRestoring;
}

// Visible in the DevTools Performance panel. Only GetGraph / GetFeatureFlags wait for this span.
export const PERSISTED_GRPC_QUERY_RESTORE_MEASURE = "chalk:persisted-grpc-query-cache:restore";

function markPersistedGrpcQueryRestoreStart(): string | null {
  if (typeof performance === "undefined" || typeof performance.mark !== "function") {
    return null;
  }
  const startMark = `${PERSISTED_GRPC_QUERY_RESTORE_MEASURE}:start`;
  performance.mark(startMark);
  return startMark;
}

function measurePersistedGrpcQueryRestore(startMark: string | null): void {
  if (startMark == null || typeof performance.measure !== "function") {
    return;
  }
  performance.measure(PERSISTED_GRPC_QUERY_RESTORE_MEASURE, startMark);
}
